'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Download, FileText, Image as ImageIcon, Loader2, ChevronDown, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ExportButtonProps {
  /** Target HTML element ref to capture */
  targetRef: React.RefObject<HTMLElement>;
  /** Base filename without extension (e.g. "astropredict-birth-chart-john-doe") */
  filename: string;
  /** Optional custom button label (defaults to "Export Result") */
  label?: string;
}

export function ExportButton({ targetRef, filename, label = 'Export Result' }: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'png' | 'jpg' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format: 'pdf' | 'png' | 'jpg') => {
    if (!targetRef.current) {
      setError('Target content element not found');
      return;
    }

    setIsOpen(false);
    setIsExporting(true);
    setExportType(format);
    setError(null);

    try {
      // Dynamic import to prevent SSR build issues
      const html2canvas = (await import('html2canvas')).default;

      const element = targetRef.current;
      
      // Capture element with html2canvas respecting theme background
      const canvas = await html2canvas(element, {
        scale: 2, // High resolution capture
        useCORS: true,
        logging: false,
        backgroundColor: getComputedStyle(document.body).getPropertyValue('--background') || '#0E111B'
      });

      const cleanFilename = filename.toLowerCase().replace(/[^a-z0-9_-]/g, '-');

      if (format === 'png' || format === 'jpg') {
        const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
        const imageUri = canvas.toDataURL(mimeType, 0.95);

        const link = document.createElement('a');
        link.download = `${cleanFilename}.${format}`;
        link.href = imageUri;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (format === 'pdf') {
        const { jsPDF } = await import('jspdf');

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Create PDF matching aspect ratio
        const pdf = new jsPDF({
          orientation: imgWidth > imgHeight ? 'landscape' : 'portrait',
          unit: 'px',
          format: [imgWidth, imgHeight]
        });

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save(`${cleanFilename}.pdf`);
      }
    } catch (err: any) {
      console.error('Export failed:', err);
      setError(err.message || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef} data-testid="export-button-wrapper">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isExporting}
          className="inline-flex items-center gap-2 bg-[var(--gold)] text-[var(--ink)] font-bold text-xs hover:bg-[var(--gold-soft)] transition-colors focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          data-testid="export-main-btn"
        >
          {isExporting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          <span>{isExporting ? `Exporting ${exportType?.toUpperCase()}...` : label}</span>
          <ChevronDown className="w-3 h-3 opacity-70" />
        </Button>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-48 rounded-xl bg-[var(--card)] border border-[var(--line)] shadow-xl z-50 overflow-hidden py-1"
          data-testid="export-dropdown-menu"
        >
          <div className="px-3 py-1.5 border-b border-[var(--line)] text-[10px] font-mono text-[var(--muted)] uppercase tracking-wider">
            Choose Format
          </div>

          <button
            type="button"
            onClick={() => handleExport('pdf')}
            className="w-full px-3 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--card-warm)] hover:text-[var(--gold)] flex items-center gap-2 transition-colors cursor-pointer"
            data-testid="export-pdf-option"
          >
            <FileText className="w-4 h-4 text-[var(--gold)]" />
            <span>Download as PDF (.pdf)</span>
          </button>

          <button
            type="button"
            onClick={() => handleExport('png')}
            className="w-full px-3 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--card-warm)] hover:text-[var(--gold)] flex items-center gap-2 transition-colors cursor-pointer"
            data-testid="export-png-option"
          >
            <ImageIcon className="w-4 h-4 text-[var(--gold)]" />
            <span>Download as PNG Image</span>
          </button>

          <button
            type="button"
            onClick={() => handleExport('jpg')}
            className="w-full px-3 py-2 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--card-warm)] hover:text-[var(--gold)] flex items-center gap-2 transition-colors cursor-pointer"
            data-testid="export-jpg-option"
          >
            <ImageIcon className="w-4 h-4 text-[var(--gold)]" />
            <span>Download as JPG Image</span>
          </button>
        </div>
      )}

      {/* Error Message Alert */}
      {error && (
        <div className="absolute right-0 mt-2 w-64 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 text-xs space-y-1 shadow-lg z-50">
          <div className="flex items-center gap-1.5 font-bold">
            <AlertCircle className="w-4 h-4" />
            <span>Export Error</span>
          </div>
          <p className="text-[11px] leading-tight">{error}</p>
        </div>
      )}
    </div>
  );
}
